import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { api, ApiError } from "../api";
import { Alert, Button, Field, Loading, Modal } from "./ui";

type HardDeleteKind = "users" | "missions" | "leaves";

interface HardDeletePreview {
  entityType: "user" | "mission" | "leave";
  id: string;
  displayName: string;
  dependentCounts: Record<string, number>;
  totalRows: number;
}

const entityLabels: Record<HardDeletePreview["entityType"], string> = {
  user: "Kullanıcı",
  mission: "Görev",
  leave: "İzin talebi",
};

export function HardDeleteAction({
  kind,
  targetId,
  targetName,
  onDeleted,
}: {
  kind: HardDeleteKind;
  targetId: string | number;
  targetName: string;
  onDeleted: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<HardDeletePreview | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const endpoint = `/api/admin/hard-delete/${kind}/${targetId}`;

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError("");
    setPreview(null);
    setConfirmation("");
    void api<HardDeletePreview>(endpoint)
      .then(setPreview)
      .catch((caught) =>
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Kalıcı silme özeti alınamadı.",
        ),
      )
      .finally(() => setLoading(false));
  }, [open, endpoint]);

  async function removePermanently() {
    if (confirmation !== "KALICI SİL") return;
    setDeleting(true);
    setError("");
    try {
      await api<{ deleted: true; operationId: string }>(endpoint, {
        method: "DELETE",
        body: { confirmation },
      });
      setOpen(false);
      await onDeleted();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Kayıt kalıcı olarak silinemedi.",
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Button type="button" variant="danger" onClick={() => setOpen(true)}>
        <Trash2 size={16} />
        Kalıcı Sil
      </Button>
      <Modal
        open={open}
        onClose={() => {
          if (!deleting) setOpen(false);
        }}
        title="Kalıcı Sil"
      >
        {loading ? (
          <Loading label="Silme etkisi hesaplanıyor" />
        ) : preview ? (
          <div className="form-stack">
            <Alert tone="danger" title="Geri alınamaz işlem">
              Bu işlem kaydı aktif sistemden tamamen kaldırır ve çöp kutusundan
              geri alınamaz.
            </Alert>
            <dl className="hard-delete-summary">
              <div>
                <dt>Kayıt türü</dt>
                <dd>{entityLabels[preview.entityType]}</dd>
              </div>
              <div>
                <dt>Kayıt</dt>
                <dd>{preview.displayName || targetName}</dd>
              </div>
              <div>
                <dt>Etkilenecek satır</dt>
                <dd>{preview.totalRows}</dd>
              </div>
            </dl>
            <div className="hard-delete-counts">
              {Object.entries(preview.dependentCounts).map(([label, count]) => (
                <span key={label}>
                  {label}: <strong>{count}</strong>
                </span>
              ))}
            </div>
            <Field
              label="Onaylamak için KALICI SİL yazın"
              value={confirmation}
              autoComplete="off"
              onChange={(event) => setConfirmation(event.target.value)}
            />
            {error ? <Alert tone="danger">{error}</Alert> : null}
            <div className="modal-actions">
              <Button
                type="button"
                variant="ghost"
                disabled={deleting}
                onClick={() => setOpen(false)}
              >
                Vazgeç
              </Button>
              <Button
                type="button"
                variant="danger"
                loading={deleting}
                disabled={confirmation !== "KALICI SİL"}
                onClick={() => void removePermanently()}
              >
                Kalıcı Sil
              </Button>
            </div>
          </div>
        ) : (
          <Alert tone="danger">
            {error || `${targetName} için silme özeti alınamadı.`}
          </Alert>
        )}
      </Modal>
    </>
  );
}
